import { Injectable } from '@angular/core';
import {
  HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode, HttpErrorResponse
} from "@angular/common/http";
import { AuthenticationService } from "../services/authentication.service";
import {
  catchError,
  Observable,
  throwError,
  switchMap,
  BehaviorSubject,
  filter,
  take
} from "rxjs"; 
import { environment } from "../../environments/environment";
import { Router } from "@angular/router";
import { AppRoutes } from "../constants/app-routes";
import { AppToastService } from "../services/common/app-toast.service";
import { AuthUser } from "../dtos/authentication/auth_user"; 

@Injectable({
  providedIn: 'root'
})
export class LogoutInterceptorService implements HttpInterceptor{
  private readonly _messageExpiredSession = "Votre session est expirée";
  private isRefreshing = false; 
  private refreshTokenSubject: BehaviorSubject<AuthUser | null> = new BehaviorSubject<AuthUser | null>(null);

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private toastService: AppToastService,
  ) { }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }


  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.url.startsWith(environment.BASE_URL_API)) {
      
      return next.handle(req)
        .pipe(
          catchError( (error: HttpErrorResponse) => {
            if (error.status === HttpStatusCode.Unauthorized && this.authenticationService.isLoggedIn()) {
              return this.handle401Error(req, next);
            }
            return throwError(() => error);
          })
        )
    }
    return next.handle(req);
  }


  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    
    if (request.url.includes('/auth/refresh-token')) {
        this.forceLogout();
        return throwError(() => new Error('Refresh token invalide ou expiré.'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null); 
      
      return this.authenticationService.requestNewAccessToken().pipe(
        switchMap((authUser: AuthUser) => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(authUser); 
          
          return next.handle(this.addToken(request, authUser.access_token!));
        }),
        catchError((refreshError) => {
          this.forceLogout();
          return throwError(() => refreshError);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(authUser => authUser !== null),
        take(1),
        switchMap((authUser) => next.handle(this.addToken(request, authUser!.access_token!))) 
      );
    }
  }

  private forceLogout(): void {
      this.authenticationService.clearDataBeforeLogout();
      this.authenticationService.notifyOfLogoutAction();
      this.toastService.showWarningMessage(this._messageExpiredSession);
      this.router.navigate([AppRoutes.AUTH, AppRoutes.LOGIN_ROUTE]);
  }
}